import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PuzzlePiece, Trash, Upload, Package } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Extensions = () => {
  const [extensions, setExtensions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetchExtensions();
  }, []);

  const fetchExtensions = async () => {
    try {
      const res = await axios.get(`${API}/extensions`);
      setExtensions(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/extensions/upload`, fd);
      if (res.data.success) {
        toast.success(`Расширение "${res.data.name}" установлено`);
        fetchExtensions();
      } else {
        toast.error(res.data.error || "Ошибка загрузки");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка загрузки расширения");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (extId) => {
    try {
      await axios.delete(`${API}/extensions/${extId}`);
      toast.success("Расширение удалено");
      fetchExtensions();
    } catch (e) {
      toast.error("Ошибка удаления");
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="extensions-page">
      <div className="top-bar" style={{ flexShrink: 0 }}>
        <div className="top-bar-left">
          <PuzzlePiece size={24} weight="duotone" className="text-[#FF9800]" />
          <h1 className="text-lg font-semibold">Расширения Chrome</h1>
          <span className="text-sm text-[#5C6370] ml-2">({extensions.length})</span>
        </div>
        <div className="top-bar-right">
          <input
            ref={fileRef}
            type="file"
            accept=".zip"
            onChange={handleUpload}
            className="hidden"
            data-testid="extension-file-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="action-btn action-btn-primary"
            data-testid="upload-extension-btn"
          >
            <Upload size={14} weight="bold" />
            {uploading ? "Загрузка..." : "Загрузить расширение (.zip)"}
          </button>
        </div>
      </div>

      <div className="content-area" style={{ flex: 1, overflow: "auto" }}>
        {extensions.length === 0 ? (
          <div className="empty-state">
            <PuzzlePiece size={48} className="empty-state-icon text-[#FF9800]" />
            <div className="empty-state-title">Нет расширений</div>
            <p className="empty-state-text">
              Загрузите .zip файл с расширением Chrome (должен содержать manifest.json)
            </p>
            <p className="text-xs text-[#5C6370] mt-2 max-w-md text-center">
              Расширения будут автоматически подключаться ко всем запускаемым профилям браузера
            </p>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            {extensions.map((ext) => (
              <div
                key={ext.id}
                className="flex items-center justify-between bg-[#1A1D24] rounded-lg border border-[#252A33] p-4"
                data-testid={`extension-item-${ext.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#FF9800]/10 flex items-center justify-center">
                    <Package size={20} className="text-[#FF9800]" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{ext.name || ext.id}</div>
                    <div className="text-[11px] text-[#5C6370]">
                      {ext.version && `v${ext.version}`}
                      {ext.description && ` — ${ext.description}`}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(ext.id)}
                  className="icon-btn text-[#5C6370] hover:text-red-400"
                  data-testid={`delete-ext-${ext.id}`}
                >
                  <Trash size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Extensions;
